// DirectMessage.js — conversation privée (DM) entre deux membres via un canal Nakama (type 2).
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import ChatAuthor from './ChatAuthor';
import { FiSend, FiX, FiTrash2, FiMinus } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';
import EmojiPicker from './EmojiPicker';
import ChatMessageBody, { parseContent, isControl } from './ChatMessageBody';
import VoiceRecorder from './VoiceRecorder';
import { stickerEffect } from '../services/chatFun';
import { openConversation, closeConversation, markRead } from '../services/dmNotify';
import { useChatSocial, ReactBtn, Reactions } from './ChatSocial';

const Box = styled.div`border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; overflow: hidden; display: flex; flex-direction: column; height: 340px;`;
const Head = styled.div`display: flex; align-items: center; justify-content: space-between; padding: .5rem .8rem; background: ${theme.colors.bgPrimary};
  b { font-size: .85rem; } button { background: none; border: none; color: ${theme.colors.textMuted}; cursor: pointer; display: flex; }`;
const Body = styled.div`flex: 1; overflow-y: auto; padding: .6rem .8rem; display: flex; flex-direction: column; gap: .4rem;`;
const Msg = styled.div`padding: .45rem .75rem; font-size: .84rem; line-height: 1.35;
  /* NE PAS casser en plein mot : overflow-wrap (pas word-break:break-word qui, en flex shrink-to-fit,
     effondrait la bulle à 1 caractère → « bonjour » coupé en « bo\\nnj\\nour »). */
  overflow-wrap: break-word; word-break: normal; white-space: pre-wrap; width: fit-content; max-width: 100%;
  align-self: ${({ $me }) => ($me ? 'flex-end' : 'flex-start')};
  border-radius: ${({ $me }) => ($me ? '14px 14px 4px 14px' : '14px 14px 14px 4px')};
  background: ${({ $me }) => ($me ? theme.colors.primary : theme.colors.bgCardHover)};
  border: 1px solid ${({ $me }) => ($me ? 'transparent' : theme.colors.border)};
  color: ${({ $me }) => ($me ? '#fff' : theme.colors.textPrimary)}; box-shadow: 0 1px 2px rgba(0,0,0,.15);`;
const Foot = styled.form`display: flex; gap: .4rem; padding: .5rem; border-top: 1px solid ${theme.colors.border};`;
const In = styled.input`flex: 1; background: ${theme.colors.bgPrimary}; color: ${theme.colors.textPrimary}; border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.full}; padding: .45rem .8rem; font-size: .84rem; &:focus { outline: none; border-color: ${theme.colors.primary}; }`;
const Send = styled.button`border: none; cursor: pointer; background: ${theme.colors.gradient}; color: #fff; width: 38px; border-radius: ${theme.radii.full}; display: flex; align-items: center; justify-content: center;`;

export default function DirectMessage({ target, onClose, onMinimize }) {
  const { auth, user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [pick, setPick] = useState(false);
  const social = useChatSocial();
  const typingRef = useRef(0);
  const channelRef = useRef(null);
  const sigRef = useRef(null);     // canal room ÉPHÉMÈRE : typing/seen/react (sans notif ni historique)
  const bodyRef = useRef(null);

  useEffect(() => {
    let unsub = null; let alive = true;
    (async () => {
      const ch = await auth.joinChannel(target.id, 2, true, true); // type 2 = DM ; hidden=true → pas de notif « wants to chat »
      if (!alive || !ch) return;
      channelRef.current = ch;
      // Canal de SIGNALISATION éphémère (room non-persistant, partagé par la paire) : les indicateurs
      // typing / accusés de lecture / réactions y transitent → ils ne créent PLUS de notif « wants to chat ».
      const sig = await auth.joinChannel(auth.dmSignalName(target.id), 1, false);
      if (alive && sig) sigRef.current = sig;
      // Charge l'HISTORIQUE persistant → en fermant/rouvrant la conversation on garde le fil.
      try {
        const hist = await auth.listChatHistory(ch.id, 40);
        if (alive && hist.length) {
          setMsgs(hist.map((m) => {
            const c = parseContent(m.content);
            return { id: m.message_id, me: m.sender_id === (user && user.id), text: c.text, sticker: c.sticker, audio: c.audio, gif: c.gif };
          }).filter((x) => x.text || x.sticker || x.gif || x.audio));
        }
      } catch { /* pas d'historique → on démarre vide */ }
      unsub = auth.onChatMessage((m) => {
        const onDm = ch && m.channel_id === ch.id;
        const onSig = sigRef.current && m.channel_id === sigRef.current.id;
        if (!onDm && !onSig) return;
        const c = parseContent(m.content);
        const mine = m.sender_id === (user && user.id);
        if (isControl(c)) { social.handleControl(c, target.display_name || target.username, mine); return; }
        if (!onDm) return;   // le contenu réel (texte/sticker) ne vit que sur le canal DM persistant
        if (c.sticker) stickerEffect(c.sticker);
        if (c.text || c.sticker || c.gif || c.audio) {
          if (!mine) markRead(target.id);   // fenêtre ouverte → message lu direct (maj « dernier lu » persisté)
          if (!mine && sigRef.current) auth.sendChat(sigRef.current.id, '', { seen: true }); // accusé de lecture (éphémère → sans notif)
          setMsgs((p) => [...p.slice(-100), { id: m.message_id, me: mine, text: c.text, sticker: c.sticker, audio: c.audio, gif: c.gif }]);
        }
      });
    })();
    // Reconnexion socket (retour d'onglet/bfcache) → on rejoint les DEUX canaux (sinon envoi/signal KO).
    const offReconnect = auth.onReconnect(async () => {
      const ch2 = await auth.joinChannel(target.id, 2, true, true);
      if (ch2 && alive) channelRef.current = ch2;
      const sig2 = await auth.joinChannel(auth.dmSignalName(target.id), 1, false);
      if (sig2 && alive) sigRef.current = sig2;
    });
    return () => { alive = false; if (unsub) unsub(); if (offReconnect) offReconnect(); };
  }, [target, auth, user]);

  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [msgs]);

  // Fenêtre ouverte = conversation LUE : pas de badge/toast pour cet ami tant qu'elle est ouverte, et le
  // « dernier lu » est persisté → le compteur de non-lus retombe à 0 (et le reste au rechargement).
  useEffect(() => {
    const id = target && target.id;
    if (!id) return undefined;
    openConversation(id);
    return () => closeConversation(id);
  }, [target]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim(); if (!t || !channelRef.current) return;
    if (await auth.sendChat(channelRef.current.id, t)) setText('');
  };
  // Contrôles (typing/réactions/vu) → canal de signalisation éphémère (repli sur le DM si absent) → pas de notif.
  const sendExtra = async (extra) => { const chan = sigRef.current || channelRef.current; if (chan) await auth.sendChat(chan.id, '', extra); };
  // CONTENU réel (sticker/gif/audio) → canal DM PERSISTANT (comme le texte). Sinon, envoyé sur le canal
  // de signalisation, il serait filtré par `if (!onDm) return` et jamais rendu/effet jamais déclenché.
  const sendContent = async (extra) => { if (channelRef.current) await auth.sendChat(channelRef.current.id, '', extra); };
  const reactTo = (msgId, emoji) => { sendExtra({ react: emoji, ref: msgId }); social.bump(msgId, emoji); };
  const notifyTyping = () => { const now = Date.now(); if (channelRef.current && now - typingRef.current > 2500) { typingRef.current = now; sendExtra({ typing: true }); } };
  const lastMineId = (() => { for (let i = msgs.length - 1; i >= 0; i--) if (msgs[i].me) return msgs[i].id; return null; })();

  return (
    <Box>
      <Head><b>💬 {target.display_name || target.username}</b>
        <span style={{ display: 'inline-flex', gap: '.4rem' }}>
          {onMinimize && <button onClick={onMinimize} title="Réduire" aria-label="Réduire"><FiMinus /></button>}
          <button onClick={async () => {
            // eslint-disable-next-line no-alert
            if (!window.confirm(`Supprimer la conversation avec ${target.display_name || target.username} ?\n\nTOUS les messages seront supprimés — c'est IRRÉVERSIBLE.`)) return;
            try { await auth.rpc('vg_channel_purge', { type: 'dm', target: target.id }); } catch (e) { /* best-effort */ }
            setMsgs([]); if (onClose) onClose();
          }} title="Supprimer la conversation (irréversible)" aria-label="Supprimer la conversation" style={{ color: '#ef4444' }}><FiTrash2 /></button>
          <button onClick={onClose} aria-label="Fermer"><FiX /></button>
        </span>
      </Head>
      <Body ref={bodyRef}>
        {msgs.length === 0 && <span style={{ color: theme.colors.textMuted, fontSize: '.8rem', margin: 'auto' }}>Démarre la conversation 👋</span>}
        {msgs.map((m) => (
          <div key={m.id} style={{ alignSelf: m.me ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
            <span style={{ display: 'flex', justifyContent: m.me ? 'flex-end' : 'flex-start', margin: '0 0 .12rem', fontSize: '.7rem', color: theme.colors.textMuted }}>
              <ChatAuthor senderId={m.me ? (user && user.id) : target.id} fallback={m.me ? (user && user.username) : (target.display_name || target.username)} size={16} />
            </span>
            <Msg $me={m.me}><ChatMessageBody text={m.text} sticker={m.sticker} audio={m.audio} gif={m.gif} /></Msg>
            <span style={{ display: 'flex', alignItems: 'center', gap: '.2rem', justifyContent: m.me ? 'flex-end' : 'flex-start' }}>
              <Reactions data={social.reactions[m.id]} /><ReactBtn onReact={(e) => reactTo(m.id, e)} />
              {m.me && m.id === lastMineId && social.seenAt > 0 && <span style={{ fontSize: '.66rem', color: theme.colors.textMuted }}>Vu ✓</span>}
            </span>
          </div>
        ))}
      </Body>
      {social.typingName && <div style={{ padding: '0 .8rem .2rem', fontSize: '.74rem', color: theme.colors.textMuted, fontStyle: 'italic' }}>{social.typingName} écrit…</div>}
      <Foot onSubmit={send}>
        <span style={{ position: 'relative', display: 'inline-flex' }}>
          <Send type="button" aria-label="Emojis & stickers" onClick={() => setPick((p) => !p)} style={{ background: 'transparent', color: theme.colors.textSecondary, width: 32 }}>😊</Send>
          {pick && <EmojiPicker onEmoji={(e) => setText((t) => (t + e).slice(0, 500))} onSticker={(id) => { sendContent({ sticker: id }); setPick(false); }} onGif={(url) => { sendContent({ gif: url }); setPick(false); }} onClose={() => setPick(false)} />}
        </span>
        <VoiceRecorder onAudio={(url) => sendContent({ audio: url })} />
        <In value={text} onChange={(e) => { setText(e.target.value); notifyTyping(); }} placeholder="Ton message privé…" maxLength={500} />
        <Send type="submit" aria-label="Envoyer"><FiSend /></Send>
      </Foot>
    </Box>
  );
}
